import React, { useState, useEffect } from 'react';
import { Typography, Spin, Flex, theme } from 'antd';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from '../i18n/i18n';
import PageLayout from '../components/PageLayout';

const { Title, Paragraph, Text } = Typography;

const Terms: React.FC = () => {
  const { language } = useTranslation();
  const { token } = theme.useToken();
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadTerms = async () => {
      try {
        const fileName = language === 'pt-BR' ? '/TERMS.pt-BR.md' : '/TERMS.en.md';
        const response = await fetch(fileName);
        if (!response.ok) {
          throw new Error('Failed to load Terms of Service');
        }
        const text = await response.text();
        setMarkdown(text);
      } catch (error) {
        console.error('Error loading Terms:', error);
        const errorTitle = language === 'pt-BR' ? 'Termos de Serviço' : 'Terms of Service';
        const errorMessage =
          language === 'pt-BR'
            ? 'Falha ao carregar os Termos de Serviço. Entre em contato com o suporte.'
            : 'Failed to load Terms of Service. Please contact support.';
        setMarkdown(`# ${errorTitle}\n\n${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    loadTerms();
  }, [language]);

  return (
    <PageLayout>
      <Flex vertical align="center" style={{ maxWidth: 900, margin: '0 auto' }}>
        {loading ? (
          <Flex justify="center" align="center" style={{ minHeight: '50vh' }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <div
            style={{
              background: token.colorBgContainer,
              padding: token.paddingLG,
              borderRadius: token.borderRadius,
              border: `1px solid ${token.colorBorderSecondary}`,
              width: '100%',
            }}
          >
            <ReactMarkdown
              components={{
                h1: ({ node, ...props }) => (
                  <Title
                    level={1}
                    style={{ marginTop: token.marginLG, marginBottom: token.marginMD }}
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <Title
                    level={2}
                    style={{ marginTop: token.marginLG, marginBottom: token.marginMD }}
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <Title
                    level={3}
                    style={{ marginTop: token.marginMD, marginBottom: token.marginSM }}
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <Paragraph style={{ marginBottom: token.marginMD }} {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    style={{ marginBottom: token.marginMD, paddingLeft: token.marginLG }}
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    style={{ marginBottom: token.marginMD, paddingLeft: token.marginLG }}
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li style={{ marginBottom: token.marginXS }} {...props} />
                ),
                strong: ({ node, ...props }) => <Text strong {...props} />,
                code: ({ node, inline, ...props }: any) =>
                  inline ? (
                    <Text code {...props} />
                  ) : (
                    <pre
                      style={{
                        background: token.colorBgContainer,
                        padding: token.paddingSM,
                        borderRadius: token.borderRadiusSM,
                        overflow: 'auto',
                        marginBottom: token.marginMD,
                      }}
                      {...props}
                    />
                  ),
              }}
            >
              {markdown}
            </ReactMarkdown>
          </div>
        )}
      </Flex>
    </PageLayout>
  );
};

export default Terms;
